import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OrganizationStateService } from '../../../shared/organization-state.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AddSubOrganizationService } from './add-sub-organization.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { OrganizationDetailsComponent } from '../organization-details/organization-details.component';

@Component({
  selector: 'app-add-sub-organization',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, OrganizationDetailsComponent],
  templateUrl: './add-sub-organization.component.html',
  styleUrls: ['./add-sub-organization.component.scss']
})
export class AddSubOrganizationComponent implements OnInit {
  @Input() parentOrgId: string = '';
  @Input() parentOrgName: string = '';
  subOrgForm: FormGroup;
  saveSuccess = false;
  serverError: string | null = null;
  private initialFormValue: any;

  constructor(
    private fb: FormBuilder,
    private orgState: OrganizationStateService,
    private router: Router,
    private addSubOrgService: AddSubOrganizationService,
    private authService: AuthService,
    private route: ActivatedRoute
  ) {
    // Prefill sub org name as before, but with required validator
    this.subOrgForm = this.fb.group({
      name: [`suborg_name_${Date.now()}`, Validators.required],
      address1: [`suborg_testAddress1_${Date.now()}`],
      address2: [`suborg_testAddress2_${Date.now()}`],
      country: [''],
      city: [`suborg_testCity_${Date.now()}`],
      state: [`suborg_testState_${Date.now()}`],
      postalCode: [`suborg_testPostalCode_${Date.now()}`],
      phone: [`suborg_testPhone_${Date.now()}`],
      firstName: [`suborg_testFirstName_${Date.now()}`],
      lastName: [`suborg_testLastName_${Date.now()}`],
      pointOfContactDetails: [''],
      active: [true]
    });
    this.initialFormValue = this.subOrgForm.getRawValue();
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const orgIdFromRoute = params.get('orgId');
      if (orgIdFromRoute) {
        this.parentOrgId = orgIdFromRoute;
        this.orgState.orgResponse$.subscribe(org => {
          this.parentOrgName = org?.name || '';
        }).unsubscribe();
      } else {
        this.addSubOrgService.getParentOrgInfo().subscribe(({ parentOrgId, parentOrgName }) => {
          this.parentOrgId = parentOrgId;
          this.parentOrgName = parentOrgName;
        });
      }
    });

    // Save initial form value after any async patching
    setTimeout(() => {
      this.initialFormValue = this.subOrgForm.getRawValue();
    });
  }

  save() {
    if (this.subOrgForm.valid) {
      const formValue = this.subOrgForm.value;
      const payload = {
        name: formValue.name || `subOrg_${Date.now()}`,
        parentOrganizationId: this.parentOrgId,
        address: {
          address1: formValue.address1 || 'testAddress1',
          address2: formValue.address2 || 'testAddress2',
          country: formValue.country || 'testCountry',
          city: formValue.city || 'testCity',
          state: formValue.state || 'testState',
          postalCode: formValue.postalCode || 'testPostalCode',
          phone: formValue.phone || 'testPhone'
        },
        active: formValue.active,
        clientCertificateVerificationRequired: false,
        excludeReferrals: false,
        lockedForMerge: false,
        contactInfo: {
          firstName: formValue.firstName || 'testFirstName',
          lastName: formValue.lastName || 'testLastName',
          pointOfContactDetails: formValue.pointOfContactDetails || 'testPointOfContactDetails'
        }
      };
      const token = this.authService.token;
      this.addSubOrgService.createSubOrg(payload, token).subscribe({
        next: (response) => {
          if (response) {
            this.saveSuccess = true;
            this.serverError = null;
            this.initialFormValue = this.subOrgForm.getRawValue(); // Update initial value after save
          } else {
            this.saveSuccess = false;
            this.serverError = response?.message || 'Failed to create sub organization.';
          }
          setTimeout(() => (this.saveSuccess = false), 3000);
        },
        error: err => {
          this.serverError = err?.error?.message || 'Failed to create sub organization.';
          this.saveSuccess = false;
        }
      });
    } else {
      this.serverError = 'Please fill all required fields.';
      this.saveSuccess = false;
    }
  }

  cancel() {
    this.subOrgForm.reset(this.initialFormValue);
    this.subOrgForm.markAsPristine();
    this.subOrgForm.markAsUntouched();
  }

  isFormChanged(): boolean {
    return JSON.stringify(this.subOrgForm.getRawValue()) !== JSON.stringify(this.initialFormValue);
  }
}